import { useEffect, useState } from 'react';
import { api, LAYER_POSITIONS, type LayerPosition, type Suggestion } from '../lib/api';
import type { StoredLayer } from '../lib/timeline';

// ---------------------------------------------------------------------------
// The layers on this scene, drawn where they will land.
//
// A PLACEMENT GUIDE, and the distinction is worth keeping straight. The frame
// behind these boxes comes from the preview service, which draws panels and
// knows nothing about layers — the compositor puts them over the assembled
// video, later. So this is not a preview of the finished composite: it is where
// each element sits in the frame, which is the question a creator is actually
// asking when they look at a shot with a banner on it.
//
// The geometry matches layers.py rather than approximating it: the same six
// positions, the same 64px margin on a 1920x1080 frame. Expressed here as
// percentages so the box lands in the same place whatever size the preview is
// rendered at.
// ---------------------------------------------------------------------------

/** The renderer's MARGIN, as a fraction of each axis of a 1920x1080 frame. */
const POSITIONS = LAYER_POSITIONS.map((p) => p.id);

const MX = 64 / 1920;
const MY = 64 / 1080;

/**
 * Where a position lands, as CSS.
 *
 * Takes a plain string because one caller reads it out of stored jsonb, which
 * outlives whatever type the code believed when it was written. An unknown
 * position falls back to `bottom` — the renderer's own default — rather than
 * throwing, because a banner in the wrong place is a bad frame and a crash is
 * a card that will not open.
 */
function style(position: string | undefined, stack = 0): React.CSSProperties {
  const p = (POSITIONS.includes(position as LayerPosition) ? position : 'bottom') as LayerPosition;
  // Two banners on one scene in the same corner do not run at the same
  // moment in the render — they have their own offsets — but the overlay
  // shows everything on the scene at once, so the second steps up out of the
  // first rather than drawing over it.
  const edge = `calc(${MY * 100}% + ${stack * 2.6}em)`;
  const vertical = p.startsWith('top')
    ? { top: edge }
    : { bottom: edge };
  if (p === 'bottom' || p === 'top') {
    // Centred: the renderer computes (W-w)/2 from the strip's measured width,
    // which is not known until the text is drawn. A transform does the same
    // thing without needing to know it.
    return { ...vertical, left: '50%', transform: 'translateX(-50%)' };
  }
  return p.endsWith('right')
    ? { ...vertical, right: `${MX * 100}%` }
    : { ...vertical, left: `${MX * 100}%` };
}

/** A picture layer on the stage, from its small copy. */
function PictureLayer({ assetId, style: st }: { assetId: string; style: React.CSSProperties }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    api.assetThumbnail(assetId).then((u) => { if (live) setUrl(u); }).catch(() => { if (live) setUrl(null); });
    return () => { live = false; };
  }, [assetId]);
  return url ? <img className="canvas-layer canvas-picture" src={url} alt="" style={st} /> : null;
}

export function CanvasLayers({ layers, suggestions, onDecide, busy, rig = null }: {
  /** F10: the wordmark shown in every lower third's right cell, or null. */
  rig?: string | null;
  /** Layers already on this scene. */
  layers: StoredLayer[];
  /** Undecided proposals for this scene, from the assistant director. */
  suggestions: Suggestion[];
  onDecide: (id: string, decision: 'approve' | 'deny') => void;
  busy: boolean;
}) {
  if (!layers.length && !suggestions.length) return null;

  // How many are already in each corner, so the next one stacks above it.
  const seen = new Map<string, number>();
  const slot = (position: string | undefined) => {
    const key = position ?? 'bottom';
    const n = seen.get(key) ?? 0;
    seen.set(key, n + 1);
    return n;
  };

  return (
    <div className="canvas-layers" aria-label="Layers on this scene">
      {layers.map((l) => {
        if (l.track === 'image' && l.props?.assetId) {
          const h = Number(l.props.height) || 120;
          return (
            <PictureLayer key={l.id} assetId={l.props.assetId}
                          style={{ ...style(l.props.position, slot(l.props.position)), height: `${(h / 1080) * 100}cqh`, padding: 0, background: 'none', border: 0 }} />
          );
        }
        // Set the way the renderer sets it, scaled to the stage: the frame is
        // 1920 wide, so a size in frame pixels is that many hundredths of a
        // container-width unit times 100/1920.
        const p = l.props ?? {};
        const st = (p.style as string) ?? 'banner';
        const size = Number(p.size) || (st === 'bug' ? 22 : 38);
        const type: React.CSSProperties = {
          fontSize: `${(size / 1920) * 100}cqw`,
          fontFamily: p.font === 'serif' ? 'Georgia, "DejaVu Serif", serif'
            : p.font === 'mono' ? 'ui-monospace, "DejaVu Sans Mono", monospace' : undefined,
          fontWeight: (p.bold ?? st !== 'bug') ? 700 : 400,
          fontStyle: p.italic ? 'italic' : undefined,
          textDecoration: p.underline ? 'underline' : undefined,
          ...(p.color ? { color: p.color } : {}),
        };
        return (
          <div key={l.id} className={`canvas-layer is-${st}`}
               style={{ ...style(p.position, slot(p.position)), ...type }}>
            {st === 'lower-third' ? (
              <span className="canvas-l3" style={p.url || rig ? { display: 'flex', alignItems: 'center', gap: 8 } : undefined}>
                <span>
                  <b>{(p.text ?? '').toUpperCase()}</b>
                  {p.sub ? <small>{p.sub}</small> : null}
                </span>
                {p.url ? <span title={p.url} style={{ marginLeft: 'auto', width: '2.6em', height: '2.6em', background: 'white', color: 'black', fontSize: '0.5em', display: 'grid', placeItems: 'center', flex: 'none' }}>QR</span>
                  : rig ? <span style={{ marginLeft: 'auto', paddingLeft: '.6em', borderLeft: '1px solid rgba(255,255,255,.4)', color: 'var(--accent, #dc2626)', fontSize: '0.55em', fontWeight: 700, letterSpacing: '.05em', flex: 'none' }}>{rig.toUpperCase()}</span> : null}
              </span>
            ) : p.text}
          </div>
        );
      })}

      {suggestions.map((s) => (
        <div
          key={s.id}
          className="canvas-layer is-proposed"
          style={style(s.payload.props?.position, slot(s.payload.props?.position))}
        >
          <span className="canvas-layer-text">{s.payload.props?.text}</span>
          {/* Approve or deny where the thing actually sits. Deciding from a
              list means reading a position out of words and picturing it;
              deciding here is looking at it. */}
          <span className="canvas-layer-act">
            <button disabled={busy} title={s.rationale}
                    onClick={() => onDecide(s.id, 'approve')}>✓</button>
            <button disabled={busy} title="Leave it in the vault"
                    onClick={() => onDecide(s.id, 'deny')}>✕</button>
          </span>
        </div>
      ))}
    </div>
  );
}
